import { Bell } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";

import { formatAgo, formatMoment } from "@/core/format/format";
import { Button } from "@/ui/button";
import { cn } from "@/ui/cn";
import { Dialog } from "@/ui/dialog";
import { Skeleton } from "@/ui/spinner";
import { ErrorState } from "@/ui/states";

import { badgeCount, notificationLink, type Notification } from "./notification-display";
import { useInbox, useMarkAllRead, useMarkRead } from "./notifications.queries";

function NotificationRow({ notification, onOpen }: { notification: Notification; onOpen: () => void }) {
  const unread = notification.readAt === null;
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className={cn(
          "flex w-full cursor-pointer flex-col gap-1 rounded-xl px-3 py-3 text-left transition-colors hover:bg-surface-muted",
          unread && "bg-primary-soft/50",
        )}
      >
        <span className="flex items-start justify-between gap-3">
          <span className={cn("text-sm", unread ? "font-bold" : "font-semibold text-ink-2")}>
            {notification.title}
          </span>
          {unread && (
            <span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" aria-label="Unread" role="img" />
          )}
        </span>
        <span className="text-sm text-ink-2">{notification.body}</span>
        <span className="text-xs text-muted" title={formatMoment(notification.createdAt)}>
          {formatAgo(notification.createdAt)}
        </span>
      </button>
    </li>
  );
}

function Inbox({ onClose }: { onClose: () => void }) {
  const inbox = useInbox();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();
  const navigate = useNavigate();
  const items = inbox.data?.pages.flatMap((page) => page.data) ?? [];
  const unread = inbox.data?.pages[0]?.meta.unreadCount ?? 0;

  const open = (n: Notification) => {
    if (n.readAt === null) markRead.mutate(n.id);
    const to = notificationLink(n);
    if (to) {
      onClose();
      void navigate(to);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => !next && onClose()}
      placement="side"
      title="Notifications"
      description="About your brands' money: payouts sent, and changes to where they go."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="secondary"
            disabled={unread === 0}
            loading={markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            Mark all as read
          </Button>
        </>
      }
    >
      {inbox.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-20 rounded-xl" />
          <Skeleton className="h-20 rounded-xl" />
        </div>
      ) : inbox.isError ? (
        <ErrorState error={inbox.error} onRetry={() => void inbox.refetch()} />
      ) : items.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted">No notifications yet.</p>
      ) : (
        <div className="flex flex-col gap-3">
          <ul className="-mx-3 flex flex-col gap-1">
            {items.map((n) => (
              <NotificationRow key={n.id} notification={n} onOpen={() => open(n)} />
            ))}
          </ul>
          {inbox.hasNextPage && (
            <Button
              variant="quiet"
              loading={inbox.isFetchingNextPage}
              onClick={() => void inbox.fetchNextPage()}
            >
              Show older
            </Button>
          )}
        </div>
      )}
    </Dialog>
  );
}

/** The bell: unread count on it, the inbox in a side panel. */
export function NotificationsButton() {
  const [open, setOpen] = useState(false);
  const inbox = useInbox();
  const unread = inbox.data?.pages[0]?.meta.unreadCount ?? 0;
  return (
    <>
      <Button
        variant="quiet"
        size="icon"
        className="relative"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
        onClick={() => setOpen(true)}
      >
        <Bell aria-hidden="true" />
        {unread > 0 && (
          <span
            aria-hidden="true"
            className="absolute top-1 right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-danger-solid px-1 text-[11px] font-bold text-on-danger-solid"
          >
            {badgeCount(unread)}
          </span>
        )}
      </Button>
      {open && <Inbox onClose={() => setOpen(false)} />}
    </>
  );
}
